import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Injector,
  DestroyRef,
  ElementRef,
  OnInit,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, finalize, switchMap, tap } from 'rxjs';
import { FormaPagamentoResgate } from '../../../core/enum/parametros/forma-pagamento-resgate.enum';
import { RegraPercentualResgate } from '../../../core/enum/parametros/regra-percentual-resgate.enum';
import { IntervaloParcelamento } from '../../../core/enum/parametros/intervalo-parcelamento.enum';
import {
  ConfiguracaoInstituto,
  Instituto,
  ProvedorAssinatura,
  ResgateConfiguracao,
  ResgateConfiguracaoStatus,
  ResgateDocumento,
  ResgateDocumentoOpcao,
} from '../../../core/models/parametros/resgate-configuracao.model';
import { ToastService } from '../../../core/services/toast.service';
import { formatDateIsoToBr } from '../../../core/utils/date-format.util';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { TooltipAjudaComponent } from '../../../shared/components/tooltip-ajuda/tooltip-ajuda.component';
import { MascaraMoedaDirective } from '../../../shared/directives/mascara-moeda/mascara-moeda.directive';
import { VivestCheckboxValueAccessorDirective } from '../../../shared/directives/checkbox-vivest/vivest-checkbox-value-accessor.directive';
import { VivestModalAreaVisivelDirective } from '../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { VivestRadioValueAccessorDirective } from '../../../shared/directives/radio-vivest/vivest-radio-value-accessor.directive';
import { VivestListaVisivelDirective } from '../../../shared/directives/select-vivest/vivest-lista-visivel.directive';
import { VivestSelectValueAccessorDirective } from '../../../shared/directives/select-vivest/vivest-select-value-accessor.directive';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import {
  FORMA_PAGAMENTO_OPCOES,
  INTERVALO_PARCELAMENTO_OPCOES,
  PARCELAS_MINIMO,
  DESCRICAO_MAXIMO,
  REGRA_PERCENTUAL_OPCOES,
  STATUS_CONFIGURACAO_OPCOES,
  RESGATE_CONFIGURACAO_PADRAO,
} from '../../../core/utils/parametros/resgate-configuracao.defaults';
import { PlanoResumo } from '../../../core/models/parametros/plano.model';
import {
  ResgateConfiguracaoService,
  TipoInstitutoNaoCadastradoError,
} from '../../../core/services/parametros/resgate-configuracao.service';
import { ResgateDocumentosModalComponent } from './resgate-documentos-modal/resgate-documentos-modal.component';
import { InstitutoFiltroComponent } from '../instituto-filtro/instituto-filtro.component';
import { PlanoCabecalhoComponent } from '../../../shared/components/parametros/plano-cabecalho/plano-cabecalho.component';
import {
  parcelasCoerentesValidator,
  regimeTributacaoObrigatorioValidator,
  retencaoCoerenteValidator,
  valorPositivoValidator,
  vigenciaCoerenteValidator,
} from '../../../core/validators/parametros/resgate-configuracao.validators';

type DocumentoForm = FormGroup<{
  id: FormControl<number>;
  nome: FormControl<string>;
  marcado: FormControl<boolean>;
}>;

@Component({
  selector: 'app-resgate-configuracao',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PlanoCabecalhoComponent,
    TooltipAjudaComponent,
    InstitutoFiltroComponent,
    EmptyStateComponent,
    ResgateDocumentosModalComponent,
    MascaraMoedaDirective,
    VivestCheckboxValueAccessorDirective,
    VivestModalAreaVisivelDirective,
    VivestRadioValueAccessorDirective,
    VivestSelectValueAccessorDirective,
    VivestListaVisivelDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './resgate-configuracao.component.html',
  styleUrl: './resgate-configuracao.component.scss',
})
export class ResgateConfiguracaoComponent implements OnInit {
  private readonly fb = inject(FormBuilder).nonNullable;
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly resgateService = inject(ResgateConfiguracaoService);
  private readonly loaderService = inject(LoaderService);
  private readonly router = inject(Router);
  private readonly parametrosRota = inject(ActivatedRoute).snapshot.queryParamMap;
  /** Instituto pedido na rota (?instituto=Resgate), vindo do "Configurar" da aba Instituto do plano. */
  private readonly institutoDaRota = this.parametrosRota.get('instituto');
  /**
   * Plano da rota (?plano=<id da parametrização>), vindo da aba Instituto do plano: cada plano tem
   * a própria configuração por instituto. Sem ele, a tela volta para a listagem de planos.
   */
  private readonly planoId = numeroDaRota(this.parametrosRota.get('plano'));
  private readonly injector = inject(Injector);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Cada instituto escolhido; `switchMap` descarta a resposta de um instituto trocado antes de carregar. */
  private readonly institutoEscolhido$ = new Subject<Instituto>();

  private readonly filtroInstituto = viewChild(InstitutoFiltroComponent);

  readonly formaPagamentoOpcoes = FORMA_PAGAMENTO_OPCOES;
  readonly intervaloOpcoes = INTERVALO_PARCELAMENTO_OPCOES;
  readonly provedores = signal<ProvedorAssinatura[]>([]);
  readonly opcoesProvedor = computed(() =>
    this.provedores().map((provedor) => ({ value: String(provedor.id), label: provedor.nome })),
  );
  readonly statusOpcoes = STATUS_CONFIGURACAO_OPCOES;
  readonly regraPercentualOpcoes = REGRA_PERCENTUAL_OPCOES;
  readonly descricaoMaximo = DESCRICAO_MAXIMO;
  readonly parcelasMinimo = PARCELAS_MINIMO;

  /** Plano do cabeçalho; `null` até o service responder. */
  readonly plano = signal<PlanoResumo | null>(null);

  readonly institutos = signal<Instituto[]>([]);
  readonly carregandoInstitutos = signal(true);

  /** Instituto cuja configuração está na tela; enquanto for `null` só o filtro é exibido. */
  readonly institutoAtual = signal<Instituto | null>(null);
  readonly carregandoConfiguracao = signal(false);
  /** `true` quando o instituto não tem configuração salva e a tela mostra os valores padrão. */
  readonly semConfiguracaoSalva = signal(false);

  readonly status = signal<string | null>(null);

  /** Cor do `vivest-tag` do status salvo: Ativo em verde, Inativo em amarelo. */
  readonly varianteStatus = computed(() => (this.status() === 'Ativo' ? 'success' : 'warning'));
  readonly ultimaAtualizacao = signal<string | null>(null);

  readonly modalDocumentosAberto = signal(false);
  readonly documentosDisponiveis = signal<ResgateDocumentoOpcao[]>([]);
  readonly carregandoDocumentos = signal(false);
  readonly confirmandoSalvar = signal(false);
  readonly salvando = signal(false);

  readonly mensagemConfirmacao = computed(() => {
    const instituto = this.institutoAtual();
    if (!instituto) {
      return '';
    }
    const acao = this.semConfiguracaoSalva() ? 'Criar' : 'Substituir';
    return `${acao} a configuração de ${instituto.nome}?`;
  });

  /** O `vivest-modal` tem um único texto de apoio: pergunta + consequência. */
  readonly descricaoConfirmacao = computed(() => {
    const mensagem = this.mensagemConfirmacao();
    return mensagem
      ? `${mensagem} As regras de pagamento, documentos e assinatura desta tela passam a valer para o instituto.`
      : '';
  });

  readonly form = this.fb.group({
    status: 'Ativo' as ResgateConfiguracaoStatus,
    vigencia: this.fb.group(
      {
        inicio: ['', Validators.required],
        /** Vazio: vigência sem data de término. */
        fim: '',
      },
      { validators: [vigenciaCoerenteValidator] },
    ),
    descricao: ['', Validators.maxLength(DESCRICAO_MAXIMO)],
    percentual: this.fb.group({
      regra: RegraPercentualResgate.Total as RegraPercentualResgate,
    }),
    tributacao: this.fb.group(
      { irRegressivo: false, irProgressivo: false },
      { validators: [regimeTributacaoObrigatorioValidator] },
    ),
    pagamento: this.fb.group(
      {
        forma: FormaPagamentoResgate.Unico as FormaPagamentoResgate,
        parcelasMinimas: [
          PARCELAS_MINIMO,
          [Validators.required, Validators.min(PARCELAS_MINIMO)],
        ],
        parcelasMaximas: [
          PARCELAS_MINIMO,
          [Validators.required, Validators.min(PARCELAS_MINIMO)],
        ],
        intervalo: IntervaloParcelamento.Mensal as IntervaloParcelamento,
      },
      { validators: [parcelasCoerentesValidator] },
    ),
    documentos: this.fb.array<DocumentoForm>([]),
    retencao: this.fb.group(
      {
        valorMinimo: this.fb.control<number | null>(RESGATE_CONFIGURACAO_PADRAO.retencao.valorMinimo, valorPositivoValidator),
        valorMaximo: this.fb.control<number | null>(RESGATE_CONFIGURACAO_PADRAO.retencao.valorMaximo, valorPositivoValidator),
      },
      { validators: [retencaoCoerenteValidator] },
    ),
    assinatura: this.fb.group({
      obrigatoria: false,
      provedor: ['', Validators.required],
      validacaoDuasEtapas: false,
    }),
  });

  get vigencia() {
    return this.form.controls.vigencia;
  }

  get descricao() {
    return this.form.controls.descricao;
  }

  get percentual() {
    return this.form.controls.percentual;
  }

  get tributacao() {
    return this.form.controls.tributacao;
  }

  /** Nenhum regime marcado, depois que o usuário mexeu nos checkboxes ou tentou salvar. */
  get tributacaoComErro(): boolean {
    const { tributacao } = this;
    return (tributacao.touched || tributacao.dirty) && tributacao.hasError('regimeTributacaoObrigatorio');
  }

  get pagamento() {
    return this.form.controls.pagamento;
  }

  get documentos(): FormArray<DocumentoForm> {
    return this.form.controls.documentos;
  }

  /** Parcelas e intervalo só valem para o parcelamento. */
  get parcelado(): boolean {
    return this.pagamento.controls.forma.value === FormaPagamentoResgate.Parcelado;
  }

  get retencao() {
    return this.form.controls.retencao;
  }

  /** Mínimo ou máximo vazio ou não positivo, depois que o usuário passou pelo campo ou tentou salvar. */
  get retencaoValorInvalido(): boolean {
    const { valorMinimo, valorMaximo } = this.retencao.controls;
    return (valorMinimo.touched && valorMinimo.invalid) || (valorMaximo.touched && valorMaximo.invalid);
  }

  get assinatura() {
    return this.form.controls.assinatura;
  }

  /** Provedor obrigatório não escolhido, depois que o usuário passou pelo campo ou tentou salvar. */
  get provedorComErro(): boolean {
    const { provedor } = this.assinatura.controls;
    return provedor.touched && provedor.invalid;
  }

  ngOnInit(): void {
    const planoId = this.planoId;
    if (planoId === null) {
      this.voltarParaLista();
      return;
    }

    this.pagamento.controls.forma.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((forma) => this.alternarParcelamento(forma === FormaPagamentoResgate.Parcelado));

    this.assinatura.controls.obrigatoria.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((obrigatoria) => this.alternarAssinatura(obrigatoria));

    this.loaderService.showLoader();
    this.resgateService
      .obterPlano(planoId)
      .pipe(
        finalize(() => this.loaderService.hideLoader()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (plano) => this.plano.set(plano),
        error: () => this.toastService.show('Não foi possível carregar os dados do plano.', 'error'),
      });

    this.loaderService.showLoader();
    this.resgateService
      .listarProvedores()
      .pipe(
        finalize(() => this.loaderService.hideLoader()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (provedores) => this.provedores.set(provedores),
        error: () => this.toastService.show('Não foi possível carregar os provedores de assinatura.', 'error'),
      });

    this.loaderService.showLoader();
    this.resgateService
      .listarInstitutos()
      .pipe(
        finalize(() => {
          this.carregandoInstitutos.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (institutos) => {
          this.institutos.set(institutos);
          const pedido = institutos.find((item) => item.tipo === this.institutoDaRota && item.habilitado);
          if (pedido) {
            this.selecionarInstituto(pedido);
          }
        },
        error: () => this.toastService.show('Não foi possível carregar os institutos.', 'error'),
      });

    this.institutoEscolhido$
      .pipe(
        switchMap((instituto) => {
          this.loaderService.showLoader();
          let respondeu = false;
          return this.resgateService.obterConfiguracao(planoId).pipe(
            tap(() => (respondeu = true)),
            // Com resposta, quem esconde o loader é `esconderLoaderAposCards`. Sem resposta (erro
            // ou instituto trocado antes de chegar), esconde aqui para o loader não ficar preso.
            finalize(() => {
              if (!respondeu) {
                this.loaderService.hideLoader();
              }
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (configuracaoSalva) => {
          this.exibirConfiguracao(configuracaoSalva);
          this.esconderLoaderAposCards();
        },
        error: () => {
          this.carregandoConfiguracao.set(false);
          this.institutoAtual.set(null);
          this.toastService.show('Não foi possível carregar a configuração do instituto.', 'error');
        },
      });
  }

  // ── instituto ───────────────────────────────────────────────────────────────

  selecionarInstituto(instituto: Instituto): void {
    this.institutoAtual.set(instituto);
    this.carregandoConfiguracao.set(true);
    this.institutoEscolhido$.next(instituto);
  }

  limparInstituto(): void {
    this.institutoAtual.set(null);
    this.status.set(null);
    this.ultimaAtualizacao.set(null);
    this.semConfiguracaoSalva.set(false);
  }

  // ── documentos ──────────────────────────────────────────────────────────────

  /** Abre o modal; o catálogo de documentos é buscado na primeira abertura. */
  abrirModalDocumentos(): void {
    this.modalDocumentosAberto.set(true);
    if (this.documentosDisponiveis().length || this.carregandoDocumentos()) {
      return;
    }
    this.carregandoDocumentos.set(true);
    this.loaderService.showLoader();
    this.resgateService
      .listarDocumentosDisponiveis()
      .pipe(
        finalize(() => {
          this.carregandoDocumentos.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (documentos) => this.documentosDisponiveis.set(documentos),
        error: () => {
          this.modalDocumentosAberto.set(false);
          this.toastService.show('Não foi possível carregar os documentos disponíveis.', 'error');
        },
      });
  }

  fecharModalDocumentos(): void {
    this.modalDocumentosAberto.set(false);
  }

  /**
   * Inclui os documentos escolhidos no modal. Um documento que estava desmarcado na lista é
   * marcado de novo em vez de duplicado; os já marcados são ignorados.
   */
  adicionarDocumentos(opcoes: readonly ResgateDocumentoOpcao[]): void {
    let alterou = false;
    for (const opcao of opcoes) {
      const existente = this.documentos.controls.find((documento) => documento.controls.id.value === opcao.id);
      if (!existente) {
        this.documentos.push(this.criarDocumento({ id: opcao.id, nome: opcao.nome }));
        alterou = true;
      } else if (!existente.controls.marcado.value) {
        existente.controls.marcado.setValue(true);
        alterou = true;
      }
    }
    if (alterou) {
      this.documentos.markAsDirty();
    }
    this.modalDocumentosAberto.set(false);
  }

  /** Ids dos documentos marcados, para o modal mostrar como já incluídos. */
  idsDocumentos(): number[] {
    return this.documentos.controls
      .filter((documento) => documento.controls.marcado.value)
      .map((documento) => documento.controls.id.value);
  }

  // ── ações do rodapé ─────────────────────────────────────────────────────────

  /** Sem plano na rota não há configuração para mostrar: volta para a listagem de planos. */
  voltarParaLista(): void {
    void this.router.navigate(['/planos']);
  }

  /** "Voltar" do cabeçalho: volta para a lista de institutos (aba Instituto do plano). */
  voltar(): void {
    void this.router.navigate(['/planos', this.planoId, 'editar'], { queryParams: { aba: 'instituto' } });
  }

  /**
   * Mesmo efeito do "Limpar" do filtro: limpa a busca do instituto e esconde a configuração,
   * descartando o que não foi salvo. Passa pelo próprio filtro, que avisa a tela pelo `limpo`.
   */
  cancelar(): void {
    this.filtroInstituto()?.limpar();
  }

  /** Valida e pede confirmação; a gravação só acontece em `confirmarSalvar`. */
  salvar(): void {
    // O vivest-button repassa o clique mesmo desabilitado: ignora enquanto uma gravação corre.
    if (this.salvando()) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.show('Revise os campos destacados antes de salvar.', 'error');
      return;
    }
    this.confirmandoSalvar.set(true);
  }

  cancelarSalvar(): void {
    this.confirmandoSalvar.set(false);
  }

  confirmarSalvar(): void {
    const instituto = this.institutoAtual();
    this.confirmandoSalvar.set(false);
    if (!instituto || this.planoId === null || this.salvando()) {
      return;
    }

    this.salvando.set(true);
    this.loaderService.showLoader();
    this.resgateService
      .salvarConfiguracao(this.planoId, this.lerFormulario())
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (salva) => {
          this.semConfiguracaoSalva.set(false);
          this.status.set(salva.configuracao.status);
          this.ultimaAtualizacao.set(this.formatarAtualizacao(salva.ultimaAtualizacao));
          // Recarrega o que foi gravado: tira da lista os documentos desmarcados e limpa o "dirty".
          this.aplicarConfiguracao(salva.configuracao);
          this.toastService.show('Configuração do resgate salva.', 'success');
        },
        error: (erro: unknown) =>
          this.toastService.show(
            erro instanceof TipoInstitutoNaoCadastradoError
              ? erro.message
              : 'Não foi possível salvar a configuração. Tente novamente.',
            'error',
          ),
      });
  }

  // ── helpers ─────────────────────────────────────────────────────────────────

  /**
   * Esconde o loader só quando os blocos da configuração estiverem na tela: espera o Angular
   * renderizar e cada `vivest-card` (web component Lit) concluir a própria renderização.
   */
  private esconderLoaderAposCards(): void {
    afterNextRender(
      () => {
        void this.aguardarCards().finally(() => this.loaderService.hideLoader());
      },
      { injector: this.injector },
    );
  }

  private async aguardarCards(): Promise<void> {
    // Sem o design system registrado não há o que esperar (e `whenDefined` nunca resolveria).
    if (!customElements.get('vivest-card')) {
      return;
    }
    const cards = Array.from(
      this.host.nativeElement.querySelectorAll<HTMLElement & { updateComplete?: Promise<unknown> }>(
        'vivest-card',
      ),
    );
    await Promise.all(cards.map((card) => card.updateComplete));
  }

  private exibirConfiguracao(configuracaoSalva: ConfiguracaoInstituto | null): void {
    this.aplicarConfiguracao(configuracaoSalva?.configuracao ?? RESGATE_CONFIGURACAO_PADRAO);
    this.semConfiguracaoSalva.set(configuracaoSalva === null);
    this.status.set(configuracaoSalva?.configuracao.status ?? null);
    this.ultimaAtualizacao.set(
      configuracaoSalva ? this.formatarAtualizacao(configuracaoSalva.ultimaAtualizacao) : null,
    );
    this.carregandoConfiguracao.set(false);
  }

  /** `yyyy-MM-ddTHH:mm:ss` → `dd/MM/yyyy HH:mm`, sem passar por `Date` (evita deslocar o fuso). */
  private formatarAtualizacao(isoDateTime: string): string {
    const [data, hora = ''] = isoDateTime.split('T');
    return `${formatDateIsoToBr(data)} ${hora.slice(0, 5)}`.trim();
  }

  private aplicarConfiguracao(configuracao: ResgateConfiguracao): void {
    this.documentos.clear({ emitEvent: false });
    configuracao.documentos.forEach((documento) =>
      this.documentos.push(this.criarDocumento(documento), { emitEvent: false }),
    );
    this.form.reset({
      status: configuracao.status,
      vigencia: { inicio: configuracao.vigencia.inicio, fim: configuracao.vigencia.fim ?? '' },
      descricao: configuracao.descricao,
      percentual: configuracao.percentual,
      tributacao: configuracao.tributacao,
      pagamento: configuracao.pagamento,
      retencao: configuracao.retencao,
      assinatura: configuracao.assinatura,
    });
  }

  /**
   * Valores do formulário no formato do model: fim de vigência vazio vira `null` e só os documentos
   * marcados entram (os desmarcados saem da configuração ao salvar).
   */
  private lerFormulario(): ResgateConfiguracao {
    const valor = this.form.getRawValue();
    return {
      ...valor,
      vigencia: { inicio: valor.vigencia.inicio, fim: valor.vigencia.fim || null },
      documentos: valor.documentos.filter((documento) => documento.marcado).map(({ id, nome }) => ({ id, nome })),
    };
  }

  /** `marcado` só existe na tela: desmarcado, o documento continua visível até salvar. */
  private criarDocumento(documento: ResgateDocumento): DocumentoForm {
    return this.fb.group({
      id: documento.id,
      nome: documento.nome,
      marcado: true as boolean,
    });
  }

  private alternarParcelamento(habilitado: boolean): void {
    const { parcelasMinimas, parcelasMaximas, intervalo } = this.pagamento.controls;
    [parcelasMinimas, parcelasMaximas, intervalo].forEach((control) =>
      habilitado ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false }),
    );
    this.pagamento.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * Sem assinatura digital, provedor e validação em duas etapas deixam de valer: ficam
   * desabilitados, e a validação em duas etapas também é desmarcada.
   */
  private alternarAssinatura(obrigatoria: boolean): void {
    const { provedor, validacaoDuasEtapas } = this.assinatura.controls;
    if (!obrigatoria) {
      validacaoDuasEtapas.setValue(false, { emitEvent: false });
    }
    [provedor, validacaoDuasEtapas].forEach((control) =>
      obrigatoria ? control.enable({ emitEvent: false }) : control.disable({ emitEvent: false }),
    );
  }
}

/** Número inteiro da rota; ausente ou inválido vira `null`. */
function numeroDaRota(valor: string | null): number | null {
  return valor !== null && /^\d+$/.test(valor) ? Number(valor) : null;
}
